import React, { useReducer } from 'react';
import { Loader2, Lock, Mail, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/apiClient';

const initialPwState = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    pwSaving: false,
    pwMsg: null,
};

function pwReducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        case 'SET_MSG':
            return { ...state, pwMsg: action.msg };
        case 'SAVE_START':
            return { ...state, pwSaving: true };
        case 'SAVE_SUCCESS':
            return {
                ...state,
                pwMsg: { type: 'success', text: 'Password updated' },
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            };
        case 'SAVE_END':
            return { ...state, pwSaving: false };
        default:
            return state;
    }
}

const initialDeleteState = {
    deleting: false,
    deleteConfirm: '',
    deleteMsg: null,
};

function deleteReducer(state, action) {
    switch (action.type) {
        case 'SET_CONFIRM':
            return { ...state, deleteConfirm: action.value };
        case 'SET_MSG':
            return { ...state, deleteMsg: action.msg };
        case 'DELETE_START':
            return { ...state, deleting: true };
        case 'DELETE_END':
            return { ...state, deleting: false };
        default:
            return state;
    }
}

export default function ProfileSettings() {
    const { user, signOut } = useAuth();

    const [pwState, pwDispatch] = useReducer(pwReducer, initialPwState);
    const { currentPassword, newPassword, confirmPassword, pwSaving, pwMsg } = pwState;

    const [deleteState, deleteDispatch] = useReducer(deleteReducer, initialDeleteState);
    const { deleting, deleteConfirm, deleteMsg } = deleteState;

    const handleChangePassword = async (e) => {
        e.preventDefault();
        pwDispatch({ type: 'SET_MSG', msg: null });
        if (newPassword !== confirmPassword) {
            pwDispatch({ type: 'SET_MSG', msg: { type: 'error', text: 'New passwords do not match' } });
            return;
        }
        if (newPassword.length < 8) {
            pwDispatch({ type: 'SET_MSG', msg: { type: 'error', text: 'New password must be at least 8 characters' } });
            return;
        }
        pwDispatch({ type: 'SAVE_START' });
        try {
            await api.changePassword(currentPassword, newPassword);
            pwDispatch({ type: 'SAVE_SUCCESS' });
        } catch (err) {
            pwDispatch({ type: 'SET_MSG', msg: { type: 'error', text: err.message || 'Failed to update password' } });
        } finally {
            pwDispatch({ type: 'SAVE_END' });
        }
    };

    const handleDeleteAccount = async () => {
        deleteDispatch({ type: 'SET_MSG', msg: null });
        if (deleteConfirm !== 'DELETE') {
            deleteDispatch({ type: 'SET_MSG', msg: { type: 'error', text: 'Type DELETE to confirm' } });
            return;
        }
        deleteDispatch({ type: 'DELETE_START' });
        try {
            await api.deleteAccount();
            await signOut();
        } catch (err) {
            deleteDispatch({ type: 'SET_MSG', msg: { type: 'error', text: err.message || 'Failed to delete account' } });
            deleteDispatch({ type: 'DELETE_END' });
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
                <p className="text-sm text-gray-600 mt-1">Manage your account credentials and personal data.</p>
            </div>

            <section className="bg-white border border-gray-200 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-gray-500" /> Account
                </h2>
                <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                        <dt className="text-gray-500">Email</dt>
                        <dd className="text-gray-900 font-medium mt-1">{user?.email || '—'}</dd>
                    </div>
                    <div>
                        <dt className="text-gray-500">User ID</dt>
                        <dd className="text-gray-900 font-mono text-xs mt-1 break-all">{user?.id || '—'}</dd>
                    </div>
                </dl>
            </section>

            <section className="bg-white border border-gray-200 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-gray-500" /> Change Password
                </h2>
                <form onSubmit={handleChangePassword} className="mt-4 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="current-password">Current password</label>
                        <input
                            id="current-password"
                            type="password"
                            required
                            autoComplete="current-password"
                            value={currentPassword}
                            onChange={(e) => pwDispatch({ type: 'SET_FIELD', field: 'currentPassword', value: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="new-password">New password</label>
                            <input
                                id="new-password"
                                type="password"
                                required
                                minLength={8}
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => pwDispatch({ type: 'SET_FIELD', field: 'newPassword', value: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirm-password">Confirm new password</label>
                            <input
                                id="confirm-password"
                                type="password"
                                required
                                minLength={8}
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => pwDispatch({ type: 'SET_FIELD', field: 'confirmPassword', value: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                    {pwMsg && (
                        <output
                            className={`block text-sm rounded-md px-3 py-2 ${pwMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
                        >
                            {pwMsg.text}
                        </output>
                    )}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={pwSaving}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium rounded-lg text-sm transition-colors"
                        >
                            {pwSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                            Update password
                        </button>
                    </div>
                </form>
            </section>

            <section className="bg-white border border-red-200 rounded-xl p-6">
                <h2 className="text-lg font-semibold text-red-700 flex items-center gap-2">
                    <Trash2 className="w-5 h-5" /> Danger Zone
                </h2>
                <p className="text-sm text-gray-600 mt-2">
                    Delete your account permanently. This removes your filters, matches, and notification settings. The action cannot be undone.
                </p>
                <div className="mt-4 space-y-3">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="delete-confirm">
                        Type <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">DELETE</span> to confirm
                    </label>
                    <input
                        id="delete-confirm"
                        type="text"
                        value={deleteConfirm}
                        onChange={(e) => deleteDispatch({ type: 'SET_CONFIRM', value: e.target.value })}
                        className="w-full sm:w-80 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    {deleteMsg && (
                        <div role="alert" className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-700 border border-red-200">
                            {deleteMsg.text}
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={handleDeleteAccount}
                        disabled={deleting || deleteConfirm !== 'DELETE'}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg text-sm transition-colors"
                    >
                        {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                        Delete my account
                    </button>
                </div>
            </section>
        </div>
    );
}
